import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Input, Label, FormGroup, Button, Form, Table } from 'reactstrap';
import { CoreClassroomAdminClassroom, CoreClassroomAdminClassroomListing, ClassroomResult } from "../../../../models/CoreClassroomAdminClassroom";
import { ClassroomUpdateOwner, ClearClassroomUpdateOwner } from "../../../../store/ClassroomAdmin/ClassroomToolsAction";
import Loading from "../../../misc/Loading";
import StatusCode from "../../../../util/StatusCode";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';


interface ClassroomViewToolUpdateOwnerProps {
    classrooms: CoreClassroomAdminClassroom[] | CoreClassroomAdminClassroomListing[]
}

const ClassroomViewToolUpdateOwner = (props: ClassroomViewToolUpdateOwnerProps) => {
    const { t } = useTranslation(['common', "classroomAdmin"]);
    const dispatch = useDispatch();

    const [ownerId, setOwnerId] = useState<string>("");
    const [submitted, setSubmitted] = useState<boolean>(false);

    const [updateOwnerClassroomState, setUpdateOwnerClassroomState] = useState<ClassroomResult[]>([]);

    useEffect(() => {
        const state = props.classrooms.map(x => ({
            classroomId: x.classroomId,
            code: StatusCode.NONE,
        }));
        setUpdateOwnerClassroomState(state);

        return () => {
            ClearClassroomUpdateOwner()(dispatch);
        };
    }, []);

    const updateOwner = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setSubmitted(true);

        const state = props.classrooms.map(x => ({
            classroomId: x.classroomId,
            code: StatusCode.PENDING,
        }));
        setUpdateOwnerClassroomState(state);

        if (!isValid) {
            return;
        }

        const results = await Promise.all(
            props.classrooms.map(x => ClassroomUpdateOwner(x.classroomId, ownerId)(dispatch))
        );

        setUpdateOwnerClassroomState(results);
    }

    const updateOwnerId = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (submitted) {
            ClearClassroomUpdateOwner()(dispatch);
        }
        setOwnerId(e.target.value)
    }

    const isValid = ownerId.length > 5;

    const renderStatus = (classroomId: string) => {
        var classroom = updateOwnerClassroomState.find(x => x.classroomId === classroomId);
        if (!classroom) {
            return;
        }
        if (classroom.code === StatusCode.PENDING) {
            return <Loading />;
        }
        if (classroom.code === StatusCode.COMPLETE) {
            return (<>
                <FontAwesomeIcon icon="check" color="green" style={{ marginRight: '5px' }} />
                {t(`tools.DELETE_CLASSROOM.success`, { ns: 'classroomAdmin' })}
            </>);
        }
        if (classroom.code === StatusCode.ERROR) {
            const errorData = classroom?.error && classroom.error.response?.data as { title?: string; detail?: string } | undefined;
            const title = errorData?.title;
            const errorMessage = title ? t(`tools.errors.general.${title}`, { ns: 'classroomAdmin' }) : t(`error.somethingWentWrong`, { ns: 'common' });
            return (<>
                <FontAwesomeIcon icon="triangle-exclamation" color="red" style={{ marginRight: '5px' }} />
                {errorMessage}
            </>);
        }
    }

    return (<>
        <Form onSubmit={updateOwner}>
            <Table striped>
                <thead>
                    <tr>
                        <th>{t('tools.tableHeader.classroom', { ns: 'classroomAdmin' })}</th>
                        <th>{t('tools.tableHeader.status', { ns: 'classroomAdmin' })}</th>
                    </tr>
                </thead>
                <tbody>
                    {props.classrooms.map(x => (
                        <tr key={x.classroomId}>
                            <td>{x.classroomName}</td>
                            <td>{renderStatus(x.classroomId)}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>

            <FormGroup>
                <Label for="ownerId">{t('tools.UPDATE_OWNER.ownerEmailAddress', { ns: 'classroomAdmin' })}</Label>
                <Input
                    type="text"
                    name="ownerId"
                    id="ownerId"
                    placeholder={t('tools.UPDATE_OWNER.ownerEmailAddress', { ns: 'classroomAdmin' })}
                    autoComplete="off"
                    value={ownerId}
                    onChange={(e) => updateOwnerId(e)}
                    disabled={submitted && updateOwnerClassroomState.some(x => x.code === StatusCode.COMPLETE)}
                />
                <div className="flex align-items-center mt-3">
                    <Button style={{ marginRight: '15px' }} type="submit" disabled={!isValid}>{t('tools.UPDATE_OWNER.title', { ns: 'classroomAdmin' })}</Button>
                </div>
            </FormGroup>
        </Form>
    </>);
}

export default ClassroomViewToolUpdateOwner;
